import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useSiteData } from "@/context/SiteDataContext";
import { inr, waLink } from "@/lib/api";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { EnquiryDialog } from "./EnquiryDialog";

export const PackageCard = ({ pkg }) => {
  const { settings } = useSiteData();
  const wa = waLink(settings.whatsapp, `Hi Ocean Wings Travel, I'd like to book "${pkg.name}". Please share availability.`);
  return (
    <article data-testid={`package-card-${pkg.slug}`}
      className="group bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col hover:-translate-y-1 hover:shadow-xl hover:border-sky-300 transition-[transform,box-shadow,border-color] duration-300">
      <Link to={`/packages/${pkg.slug}`} className="relative block aspect-[4/3] overflow-hidden" data-testid={`package-card-image-${pkg.slug}`}>
        <img src={pkg.image} alt={pkg.name} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <span className="absolute top-4 left-4 rounded-full bg-white/95 px-3 py-1 text-xs font-bold text-navy">{[pkg.category, ...(pkg.alt_categories || [])].join(" · ")}</span>
        <span className="absolute top-4 right-4 rounded-full bg-navy/85 px-3 py-1 font-mono text-xs font-semibold text-white">{pkg.duration}</span>
      </Link>
      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-display text-2xl font-semibold text-navy leading-tight"><Link to={`/packages/${pkg.slug}`} className="hover:text-ocean transition-colors" data-testid={`package-card-title-${pkg.slug}`}>{pkg.name}</Link></h3>
        <p className="mt-2 text-sm text-slate-600 line-clamp-2">{pkg.tagline}</p>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {pkg.highlights.slice(0, 4).map((h) => (
            <span key={h} className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-mist text-slate-700 border border-sky-100">{h}</span>
          ))}
        </div>
        <div className="mt-auto pt-6">
          <div className="flex items-end justify-between gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-slate-500">Starting from</p>
              <p className="font-mono text-xl font-semibold text-ocean" data-testid={`package-card-price-${pkg.slug}`}>
                {inr(pkg.selling_price)}<span className="font-sans text-xs font-normal text-slate-500"> /person</span>
              </p>
            </div>
            <Link to={`/packages/${pkg.slug}`} data-testid={`package-card-view-details-${pkg.slug}`}
              className="inline-flex items-center gap-1 text-sm font-semibold text-ocean hover:text-navy">
              Details <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-4 flex gap-2">
            <EnquiryDialog packageId={pkg.id} title={`Free quote: ${pkg.name}`}
              trigger={<button type="button" data-testid={`package-card-enquire-${pkg.slug}`} className="flex-1 h-10 rounded-full bg-ocean text-white text-sm font-semibold hover:bg-navy transition-colors">Get Free Quote</button>} />
            <a href={wa} target="_blank" rel="noreferrer" aria-label="WhatsApp enquiry" data-testid={`package-card-whatsapp-${pkg.slug}`}
              className="h-10 w-10 rounded-full grid place-items-center bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-500 hover:text-white transition-colors">
              <WhatsAppIcon className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
    </article>
  );
};
